/**
 * PasskeyNudge — one-time in-app toast that invites users to enrol a
 * fingerprint passkey AFTER their first successful password sign-in.
 *
 * Conditions for showing the toast:
 *   1. We have a logged-in user (auth resolved).
 *   2. Browser supports WebAuthn.
 *   3. Tenant has `webauthn_login` feature flag enabled.
 *   4. User has zero passkeys enrolled (probe /webauthn/available).
 *   5. User hasn't dismissed the nudge before (localStorage flag per uid).
 *
 * Dismissal: clicking "Enrol now" or "Not now" sets a permanent flag so
 * the toast never reappears for that account on that browser.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import { isWebAuthnSupported, checkPasskeyAvailable } from "@/lib/webauthn";

const dismissKey = (uid) => `passkey_nudge_dismissed_${uid}`;

export default function PasskeyNudge() {
    const { user } = useAuth();
    const { flags } = useFeatures();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user || !user.id || !user.email) return;
        if (!flags?.webauthn_login) return;
        if (!isWebAuthnSupported()) return;
        if (localStorage.getItem(dismissKey(user.id)) === "1") return;
        // Don't nag client-portal users — they sign in less frequently.
        if (user.role === "client_portal") return;
        // Super admins manage MFA via TOTP/email, not biometrics, and don't
        // have access to the tenant Settings → Passkeys page.
        if (user.role === "super_admin") return;

        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const has = await checkPasskeyAvailable(user.email);
                if (cancelled || has) return;        // already enrolled → no nudge
                if (localStorage.getItem(dismissKey(user.id)) === "1") return;

                const id = toast(
                    "Sign in in 2 seconds next time",
                    {
                        description: "Enrol a fingerprint passkey now — no more typing your password.",
                        duration: 14000,
                        action: {
                            label: "Enrol now",
                            onClick: () => {
                                localStorage.setItem(dismissKey(user.id), "1");
                                navigate("/app/settings#passkeys");
                                toast.dismiss(id);
                            },
                        },
                        cancel: {
                            label: "Not now",
                            onClick: () => {
                                localStorage.setItem(dismissKey(user.id), "1");
                                toast.dismiss(id);
                            },
                        },
                    },
                );
            } catch (_e) { /* network blip — silently skip; we'll try again next session */ }
        }, 2500);   // wait until after dashboard paints

        return () => { cancelled = true; clearTimeout(t); };
    }, [user, flags, navigate]);

    return null;
}
